import React from 'react';
import s from './TaxInformation.styles';
import { useTranslationsController } from '../../../../utils/globalTranslationsController';
import { taxInformationTranslationObject } from './translations';
import {
  FlexContainer,
  FormLabel,
  PrimaryButtonAlternate,
  Radio,
  Spacer,
  Spinner,
  TertiaryButtonAlternate,
} from '../../../common';
import { ActionContainer, ValidationMsg } from '../Register.styles';
import { RegisterProps } from '../types';
import { RadioGroup } from '@material-ui/core';
import { taxInfoFields } from '../data';
import { ErrorMessageText } from '../../../layout/SocialContainer/LoginButtons/FacebookLogin/FacebookLogin.styles';
import { validFormat } from '../../../../utils/vatValidation.utils';
import { formatString } from '../../../../utils/format.utils';
import { Notice } from '../../../common/Notice';

interface TaxInformationProps extends RegisterProps {
  isMobile: boolean;
}

export const TaxInformation: React.FC<TaxInformationProps> = ({
  isMobile,
  isFormValid,
  handleStageChange,
  handleRegister,
  taxInfo,
  taxInfoDispatch,
  details,
  validVAT,
  error,
  isLoading,
}) => {
  const { taxInformation: t } = useTranslationsController(
    taxInformationTranslationObject
  );

  return (
    <>
      {error && (
        <Notice
          variant={'alert'}
          message={error}
          testId={'t_error_notice'}
          type="darkmode"
        />
      )}

      <s.TaxInformationForm
        data-testid="t_tax_information"
        aria-describedby="valid_msg"
        onSubmit={(e: any) => e.preventDefault()}
      >
        <s.TaxInformationHeader id="form_title">
          {t['REGISTER.TAX_INFORMATION.HEADER']}
        </s.TaxInformationHeader>
        <s.TaxInformationWrapper>
          <FormLabel hasBottomPadding={false} isBold={false} darkMode>
            {t['PROFILE.TAX_INFORMATION.VAT']}
            <sup> *</sup>
            <RadioGroup
              aria-labelledby="vat_registered"
              value={taxInfo.isVATRegistered}
              data-testid={`t_vat_reg`}
              name="vat"
              row
              onChange={e =>
                taxInfoDispatch({
                  type: taxInfoFields.IS_VAT_REGISTERED,
                  payload: e.currentTarget.value,
                })
              }
            >
              <Radio
                label={t['GENERIC.YES']}
                testId={'t_vat_registered_yes'}
                value="yes"
                darkMode
              />
              <Radio
                label={t['GENERIC.NO']}
                testId={'t_vat_registered_no'}
                value="no"
                darkMode
              />
            </RadioGroup>
          </FormLabel>
          <Spacer amount={16} isVertical />
          {taxInfo.isVATRegistered === 'yes' && (
            <>
              <FlexContainer
                flexDirection="column"
                fullWidth
                alignItems="flex-start"
              >
                <s.Label id="label_vat" isBold>
                  {t['PROFILE.TAX_INFORMATION.VAT_NUMBER']}
                </s.Label>
                <s.Input
                  data-testid="t_input_vat"
                  aria-labelledby="label_vat form_title"
                  placeholder={''}
                  value={taxInfo.vatNumber}
                  onChange={e =>
                    taxInfoDispatch({
                      type: taxInfoFields.VAT_NUMBER,
                      payload: e.currentTarget.value,
                    })
                  }
                />
              </FlexContainer>
              <Spacer amount={8} isVertical />
              {!validVAT && (
                <ErrorMessageText>
                  {formatString(t['PROFILE.TAX_INFORMATION.VAT_ERROR'], [
                    details.country,
                    validFormat[details.country],
                  ])}
                </ErrorMessageText>
              )}
              <Spacer amount={16} isVertical />
            </>
          )}
          <FormLabel hasBottomPadding={false} isBold={false} darkMode>
            {t['PROFILE.TAX_INFORMATION.LLC']}
            <sup> *</sup>
            <RadioGroup
              aria-labelledby="is_llc"
              value={taxInfo.isLLC}
              data-testid={`t_is_llc`}
              name="llc"
              row
              onChange={e =>
                taxInfoDispatch({
                  type: taxInfoFields.IS_LLC,
                  payload: e.currentTarget.value,
                })
              }
            >
              <Radio
                label={t['GENERIC.YES']}
                testId={'t_llc_yes'}
                value="yes"
                darkMode
              />
              <Radio
                label={t['GENERIC.NO']}
                testId={'t_llc_no'}
                value="no"
                darkMode
              />
            </RadioGroup>
          </FormLabel>
          <Spacer amount={16} isVertical />
          {taxInfo.isLLC === 'yes' && (
            <>
              <s.DualFieldWrapper justifyContent="space-between" fullWidth>
                <FlexContainer
                  flexDirection="column"
                  fullWidth
                  alignItems="flex-start"
                >
                  <s.Label id="label_cname" isBold>
                    {t['PROFILE.TAX_INFORMATION.LLC_NAME']}
                  </s.Label>
                  <s.Input
                    data-testid="t_input_cname"
                    aria-labelledby="label_cname form_title"
                    placeholder={''}
                    value={taxInfo.companyName}
                    onChange={e =>
                      taxInfoDispatch({
                        type: taxInfoFields.COMPANY_NAME,
                        payload: e.currentTarget.value,
                      })
                    }
                  />
                </FlexContainer>
                <Spacer amount={16} />
                <FlexContainer
                  flexDirection="column"
                  fullWidth
                  alignItems="flex-start"
                >
                  <s.Label id="label_cnumber" isBold>
                    {t['PROFILE.TAX_INFORMATION.LLC_NUMBER']}
                  </s.Label>
                  <s.Input
                    data-testid="t_input_cnumber"
                    aria-labelledby="label_cnumber form_title"
                    placeholder={''}
                    value={taxInfo.companyNumber}
                    onChange={e =>
                      taxInfoDispatch({
                        type: taxInfoFields.COMPANY_NUMBER,
                        payload: e.currentTarget.value,
                      })
                    }
                  />
                </FlexContainer>
              </s.DualFieldWrapper>
              <Spacer amount={16} isVertical />
            </>
          )}
          {['DK', 'NO'].includes(details.country) && (
            <FormLabel hasBottomPadding={false} isBold={false} darkMode>
              {t['PROFILE.TAX_INFORMATION.RECEIVE_VAT']}
              <sup> *</sup>
              <RadioGroup
                aria-labelledby="receive_vat"
                value={taxInfo.receiveVAT}
                data-testid={`t_receive_vat`}
                name="receive_vat"
                row
                onChange={e =>
                  taxInfoDispatch({
                    type: taxInfoFields.RECEIVE_VAT,
                    payload: e.currentTarget.value,
                  })
                }
              >
                <Radio
                  label={t['GENERIC.YES']}
                  testId={'t_receive_vat_yes'}
                  value="yes"
                  darkMode
                />
                <Radio
                  label={t['GENERIC.NO']}
                  testId={'t_receive_vat_no'}
                  value="no"
                  darkMode
                />
              </RadioGroup>
            </FormLabel>
          )}
          <ValidationMsg id="valid_msg">
            {t['REGISTER.DETAILS.MESSAGE.VALID']}
          </ValidationMsg>
          <Spacer amount={40} isVertical />
          <ActionContainer isMobile={isMobile}>
            <TertiaryButtonAlternate
              data-testid="t_prev_btn"
              onClick={e => {
                e.preventDefault();
                handleStageChange(2);
              }}
              type="button"
              fullWidth={isMobile}
            >
              {t['GENERIC.BACK']}
            </TertiaryButtonAlternate>
            <PrimaryButtonAlternate
              onClick={e => {
                e.preventDefault();
                handleRegister();
              }}
              disabled={!isFormValid || isLoading}
              aria-disabled={!isFormValid || isLoading}
              data-testid="t_register_button"
            >
              {isLoading ? <Spinner /> : <>{t['GENERIC.SUBMIT']}</>}
            </PrimaryButtonAlternate>
          </ActionContainer>
        </s.TaxInformationWrapper>
      </s.TaxInformationForm>
    </>
  );
};
